import React, { useId } from 'react';

export interface McbSvgProps {
  poles?: 1 | 3;
  designator: string;
  ratedCurrent?: number | string;
  curve?: string;
  isClosed?: boolean;
  isTripped?: boolean;
  onToggle?: () => void;
  terminalValues?: Record<string, string>; // terminalId -> potential ('L1', 'L2', 'N', etc.)
  highlightTerminals?: string[];
  activeTerminalId?: string | null | undefined;
  onTerminalClick?: (terminalId: string, e: React.MouseEvent) => void;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Endüstriyel Minyatür Devre Kesici (Otomat Sigorta - MCB) Vektörel Çizimi
 * DIN Rayına monte edilebilir, 1 Kutuplu (1P) veya 3 Kutuplu (3P) seçenekli.
 */
export const McbSvg: React.FC<McbSvgProps> = ({
  poles = 1,
  designator,
  ratedCurrent = poles === 3 ? 16 : 6,
  curve = poles === 3 ? 'C' : 'B',
  isClosed = true,
  isTripped = false,
  onToggle,
  terminalValues = {},
  highlightTerminals = [],
  activeTerminalId = null,
  onTerminalClick,
  style,
  className = '',
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');

  const width = poles === 3 ? 84 : 28;
  const height = 110;
  const poleWidth = 28;

  // Gerçek mekanik durum: tripped ise kol orta/aşağı konuma düşer
  const leverOn = isClosed && !isTripped;

  const getTerminalColor = (tid: string): string => {
    const pot = terminalValues[tid];
    if (pot === 'L1') return '#f59e0b';
    if (pot === 'L2') return '#ea580c';
    if (pot === 'L3') return '#cbd5e1';
    if (pot === 'N') return '#38bdf8';
    if (pot === 'PE') return '#84cc16';
    return '#94a3b8';
  };

  const renderScrewTerminal = (tid: string, label: string, cx: number, cy: number) => {
    const isTarget = highlightTerminals.includes(tid);
    const isActive = activeTerminalId === tid;
    const termColor = getTerminalColor(tid);
    const hasPotential = terminalValues[tid] && terminalValues[tid] !== 'FLOATING';

    return (
      <g
        key={tid}
        transform={`translate(${cx}, ${cy})`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onTerminalClick?.(tid, e);
        }}
      >
        {/* Terminal Vida Yuvası (Havşa) */}
        <circle cx="0" cy="0" r="7" fill="#1e2530" stroke="#334155" strokeWidth="1" />
        <circle cx="0" cy="0" r="4.5" fill="#475569" stroke="#1e293b" strokeWidth="0.8" />
        
        {/* Vida kafası yarığı */}
        <line x1="-3" y1="-1.5" x2="3" y2="1.5" stroke="#1e293b" strokeWidth="1.2" />

        {/* Gerilim altındaysa parlama halesi */}
        {hasPotential && (
          <circle cx="0" cy="0" r="7.5" fill="none" stroke={termColor} strokeWidth="1.5" strokeOpacity="0.8" />
        )}

        {/* Bağlantı için hedef / aktif göstergesi */}
        {(isTarget || isActive) && (
          <circle
            cx="0"
            cy="0"
            r="10"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="1.8"
            strokeDasharray={isActive ? 'none' : '3,2'}
            className="terminal-pulse"
          />
        )}

        {/* Terminal etiketi */}
        <text
          x="0"
          y={cy < 30 ? -9 : 14}
          fill="#cbd5e1"
          fontSize="7"
          fontFamily="system-ui, monospace"
          fontWeight="bold"
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`mcb-device-svg ${className}`}
      style={{ overflow: 'visible', userSelect: 'none', display: 'block', ...style }}
    >
      <defs>
        {/* Gövde Düşey Gradyanı */}
        <linearGradient id={`mcbBodyGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="10%" stopColor="#e2e8f0" />
          <stop offset="90%" stopColor="#cbd5e1" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>

        {/* Mandal Kolu Metalik Gradyanı */}
        <linearGradient id={`mcbLeverGrad-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="50%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Gövde Derinlik Gölgesi */}
        <filter id={`mcbShadow-${uid}`} x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.35" />
        </filter>
      </defs>

      {/* Ana Gövde Bloğu */}
      <g filter={`url(#mcbShadow-${uid})`}>
        <rect
          x="1"
          y="1"
          width={width - 2}
          height={height - 2}
          rx="4"
          fill={`url(#mcbBodyGrad-${uid})`}
          stroke="#64748b"
          strokeWidth="1.2"
        />

        {/* Kutuplar Arası Bölme Kanalları (3P için) */}
        {poles === 3 && (
          <>
            <line x1={poleWidth} y1="2" x2={poleWidth} y2={height - 2} stroke="#94a3b8" strokeWidth="1" />
            <line x1={poleWidth * 2} y1="2" x2={poleWidth * 2} y2={height - 2} stroke="#94a3b8" strokeWidth="1" />
          </>
        )}

        {/* Orta Yükseltilmiş Gövde ve Durum Penceresi */}
        <rect
          x="4"
          y="32"
          width={width - 8}
          height="46"
          rx="2"
          fill="#f1f5f9"
          stroke="#94a3b8"
          strokeWidth="0.8"
        />

        {/* Gösterge Penceresi (Yeşil: Açık/OFF, Kırmızı: Kapalı/ON Enerjili) */}
        <rect
          x={width / 2 - 6}
          y="36"
          width="12"
          height="6"
          rx="1"
          fill={leverOn ? '#ef4444' : '#22c55e'}
          stroke="#0f172a"
          strokeWidth="0.6"
        />

        {/* Cihaz Etiketi ve Değerleri */}
        <text
          x={width / 2}
          y="49"
          fill="#1e293b"
          fontSize="7.5"
          fontFamily="system-ui, sans-serif"
          fontWeight="bold"
          textAnchor="middle"
        >
          {designator}
        </text>
        <text
          x={width / 2}
          y="58"
          fill="#475569"
          fontSize="6.5"
          fontFamily="monospace"
          fontWeight="bold"
          textAnchor="middle"
        >
          {curve}{ratedCurrent}A
        </text>
        <text
          x={width / 2}
          y="66"
          fill="#64748b"
          fontSize="5"
          fontFamily="system-ui, sans-serif"
          textAnchor="middle"
        >
          400V~ 6000
        </text>

        {/* Açma-Kapama Mandalı (Açık: Yukarıda, Kapalı: Aşağıda) */}
        <g
          transform={`translate(${width / 2 - (poles === 3 ? 24 : 8)}, ${leverOn ? 74 : 84})`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
        >
          {/* Mekanik Köprü Barı (3 kutup mandalını birbirine bağlar) */}
          <rect
            x="0"
            y="0"
            width={poles === 3 ? 48 : 16}
            height="14"
            rx="3"
            fill={`url(#mcbLeverGrad-${uid})`}
            stroke="#0f172a"
            strokeWidth="1"
          />
          <line
            x1="3"
            y1="3"
            x2={poles === 3 ? 45 : 13}
            y2="3"
            stroke="#94a3b8"
            strokeWidth="0.8"
            opacity="0.6"
          />
          {/* Kol Durum Yazısı */}
          <text
            x={poles === 3 ? 24 : 8}
            y="10"
            fill={leverOn ? '#f87171' : '#4ade80'}
            fontSize="6"
            fontFamily="system-ui, sans-serif"
            fontWeight="bold"
            textAnchor="middle"
          >
            {leverOn ? 'I - ON' : 'O - OFF'}
          </text>
        </g>
      </g>

      {/* Klemens Vidalama Terminalleri */}
      {poles === 1 ? (
        <>
          {renderScrewTerminal('1', '1', 14, 14)}
          {renderScrewTerminal('2', '2', 14, 98)}
        </>
      ) : (
        <>
          {/* 3P Üst Giriş Terminalleri */}
          {renderScrewTerminal('1', '1/L1', 14, 14)}
          {renderScrewTerminal('3', '3/L2', 42, 14)}
          {renderScrewTerminal('5', '5/L3', 70, 14)}

          {/* 3P Alt Çıkış Terminalleri */}
          {renderScrewTerminal('2', '2/T1', 14, 98)}
          {renderScrewTerminal('4', '4/T2', 42, 98)}
          {renderScrewTerminal('6', '6/T3', 70, 98)}
        </>
      )}
    </svg>
  );
};
